import React, { useState, useEffect } from 'react';
import { adminAPI, commonAPI, formMasterAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AdminMasterResultSheet = () => {
  // Dropdown options
  const [myClasses, setMyClasses] = useState([]);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);

  // Selected values
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');

  // View mode: 'flat' or 'pivot'
  const [viewMode, setViewMode] = useState('flat');

  // Result data
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Load initial data (classes and sessions)
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load terms when session changes
  useEffect(() => {
    if (selectedSession) {
      fetchTerms(selectedSession);
    }
  }, [selectedSession]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [classesRes, sessionsRes] = await Promise.all([
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions(),
      ]);
      setMyClasses(classesRes.data);
      setAcademicSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to load classes or sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async (sessionId) => {
    try {
      const response = await commonAPI.getTerms(sessionId);
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load terms');
    }
  };

  const fetchResultSheet = async () => {
    if (!selectedClass || !selectedSession || !selectedTerm) {
      toast.error('Please select class, session, and term');
      return;
    }

    try {
      setFetching(true);
      let response;
      if (viewMode === 'flat') {
        response = await adminAPI.getMasterResultSheet(
          selectedClass,
          selectedSession,
          selectedTerm
        );
      } else {
        response = await adminAPI.getMasterResultPivot(
          selectedClass,
          selectedSession,
          selectedTerm
        );
      }
      setResultData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load result sheet');
      setResultData(null);
    } finally {
      setFetching(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!resultData || !resultData.data.length) return;

    const headers = resultData.columns;
    const rows = resultData.data.map(row =>
      headers.map(header => `"${row[header] ?? ''}"`).join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `master_result_${viewMode}_${selectedClass}_${selectedSession}_${selectedTerm}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Determine current class name for display
  const getCurrentClassName = () => {
    const cls = myClasses.find(c => c.class_id == selectedClass);
    return cls ? cls.class_name : '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Master Result Sheet</h1>
          <p className="text-gray-600">View results in flat or pivot (broadsheet) format</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          {/* Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class *</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="input-field"
              disabled={loading}
            >
              <option value="">Select Class</option>
              {myClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.class_name} ({cls.student_count} students)
                </option>
              ))}
            </select>
          </div>

          {/* Academic Session */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Academic Session *</label>
            <select
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              className="input-field"
              disabled={loading}
            >
              <option value="">Select Session</option>
              {academicSessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.academic_session}
                </option>
              ))}
            </select>
          </div>

          {/* Term */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term *</label>
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              className="input-field"
              disabled={!selectedSession}
            >
              <option value="">Select Term</option>
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.term}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">View Mode</label>
            <div className="flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode('flat')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md border ${
                  viewMode === 'flat'
                    ? 'bg-primary-600 text-green border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Flat
              </button>
              <button
                type="button"
                onClick={() => setViewMode('pivot')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                  viewMode === 'pivot'
                    ? 'bg-primary-600 text-green border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Pivot
              </button>
            </div>
          </div>

          {/* Fetch Button */}
          <div>
            <button
              onClick={fetchResultSheet}
              disabled={fetching || !selectedClass || !selectedSession || !selectedTerm}
              className="btn-primary w-full"
            >
              {fetching ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-table mr-2"></i>
                  Load Result Sheet
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info message */}
        {selectedClass && selectedSession && selectedTerm && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <i className="fas fa-info-circle mr-2"></i>
            Showing <strong>{viewMode === 'flat' ? 'flat' : 'pivot'}</strong> view for{' '}
            <strong>{getCurrentClassName()}</strong> –{' '}
            {academicSessions.find(s => s.id == selectedSession)?.academic_session} –{' '}
            {terms.find(t => t.id == selectedTerm)?.term}
          </div>
        )}
      </div>

      {/* Result Table */}
      {resultData && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              Result Sheet · {resultData.total_rows} rows
            </h2>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <i className="fas fa-download mr-2"></i>
              Export CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {resultData.columns.map((col, idx) => (
                    <th
                      key={idx}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resultData.data.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    {resultData.columns.map((col, colIdx) => (
                      <td key={colIdx} className="px-4 py-2 whitespace-nowrap">
                        {row[col] ?? '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {resultData.data.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No data available for the selected criteria.
            </div>
          )}
        </div>
      )}

      {/* Loading state for initial data */}
      {loading && (
        <div className="flex justify-center py-12">
          {/* <LoadingSpinner /> */}
          Loading...
        </div>
      )}
    </div>
  );
};

export default AdminMasterResultSheet;